import { Button } from "$components/button/button";
import type { InvestigationActivity } from "$global-providers/rxdb-provider/collections/investigation-activity";
import RhfDynamicFormFields from "$routes/_lib/rhf-dynamic-form-fields/rhf-dynamic-form-fields";
import ValidationContext from "$routes/_lib/validation/validation-context";
import { convertOrderedSchema, parseSchema } from "@elephant-healthcare/yupify";
import { yupResolver } from "@hookform/resolvers/yup";
import { styled } from "@material-ui/styles";
import { clone, omit } from "ramda";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { L10N, translateI18nObject } from "../../_lib/translation-util";

const FormContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

const ButtonContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "flex-end",
}));
export const InvestigationActivityForm: FC<{
  investigationId: InvestigationActivity["id"];
  investigationRequest: InvestigationActivity["request"];
  completeInvestigation: (args: {
    investigationId: InvestigationActivity["id"];
    investigationRequest: InvestigationActivity["request"];
    formResults: { [prop: string]: any };
  }) => Promise<void>;
  disabled?: boolean;
  paymentBlocked?: boolean;
}> = ({
  investigationId,
  investigationRequest,
  completeInvestigation,
  disabled = false,
  paymentBlocked = false,
}) => {
  const translatedRequest = translateI18nObject(investigationRequest, L10N.EN);
  const orderedSchema = omit(["meta"], translatedRequest);
  const { validationSchema, defaultValues } = useMemo(() => {
    const formSchema = convertOrderedSchema(orderedSchema);
    const clonedSchema = clone(formSchema);
    const validationSchema = parseSchema(clonedSchema);
    return { validationSchema, defaultValues: validationSchema.getDefault() };
  }, [orderedSchema]);

  const formMethods = useForm({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  return (
    <ValidationContext.Provider value={{ validationSchema }}>
      <FormProvider {...formMethods}>
        <FormContainer>
          <RhfDynamicFormFields schema={orderedSchema} disabled={disabled} />
          <ButtonContainer>
            <Button
              onClick={formMethods.handleSubmit((formResults) =>
                completeInvestigation({
                  investigationId,
                  investigationRequest,
                  formResults,
                }),
              )}
              disabled={
                disabled || formMethods.formState.isSubmitting || paymentBlocked
              }
            >
              Submit
            </Button>
          </ButtonContainer>
        </FormContainer>
      </FormProvider>
    </ValidationContext.Provider>
  );
};
